import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getLedgerReport } from "./ledgerService";
import LedgerTable from "./LedgerTable";
import {
  FaBook,
  FaPrint,
  FaFileCsv,
  FaArrowLeft,
  FaFilter,
  FaUndo,
  FaSearch,
  FaCalendarAlt,
  FaBalanceScale,
  FaArrowUp,
  FaArrowDown,
  FaListOl
} from "react-icons/fa";
import "./LedgerReport.css";
import { usePermissions } from "../../context/PermissionContext";

const TRANSACTION_TYPES = [
  { value: "", label: "All Transaction Types" },
  { value: "Sale", label: "Sale" },
  { value: "Receipt", label: "Receipt" },
  { value: "Payment", label: "Payment" },
  { value: "Refund", label: "Refund" },
  { value: "Credit Note", label: "Credit Note" },
  { value: "Debit Note", label: "Debit Note" },
];

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const LedgerReportPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  // ── Filters ──────────────────────────────────────────────
  const [startDate, setStartDate]             = useState("");
  const [endDate, setEndDate]                 = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [partyName, setPartyName]             = useState("");
  const [searchQuery, setSearchQuery]         = useState("");

  // ── Data ─────────────────────────────────────────────────
  const [entries, setEntries]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const hasFetched = useRef(false);

  // ── Fetch ─────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date:       startDate || undefined,
        end_date:         endDate   || undefined,
        transaction_type: transactionType || undefined,
        party_name:       partyName || undefined,
      };
      const data = await getLedgerReport(filters);
      setEntries(data.entries || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error("Ledger fetch error:", err);
      setError("Failed to load the ledger report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, transactionType, partyName]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Date Presets ──────────────────────────────────────────
  const handlePreset = (presetType) => {
    const now = new Date();
    let start = "";
    let end = now.toISOString().split("T")[0];

    if (presetType === "THIS_MONTH") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      start = firstDay.toISOString().split("T")[0];
    } else if (presetType === "THIS_QUARTER") {
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const firstDay = new Date(now.getFullYear(), quarterMonth, 1);
      start = firstDay.toISOString().split("T")[0];
    } else if (presetType === "YTD") {
      const firstDay = new Date(now.getFullYear(), 0, 1);
      start = firstDay.toISOString().split("T")[0];
    }

    setStartDate(start);
    setEndDate(end);
    hasFetched.current = false;
    setTimeout(() => fetchReport(), 0);
  };

  // ── Reset ─────────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTransactionType("");
    setPartyName("");
    setSearchQuery("");
    hasFetched.current = false;
    setTimeout(() => fetchReport(), 0);
  };

  // ── Print & CSV ──────────────────────────────────────────
  const handlePrint = () => {
    if (!canExport("report_ledger")) {
      alert("Access Denied: You do not have permission to print or export the Ledger report.");
      return;
    }
    window.print();
  };

  const handleExportCSV = () => {
    if (!canExport("report_ledger")) {
      alert("Access Denied: You do not have permission to download or export the Ledger report.");
      return;
    }
    if (!entries.length) return;
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "REKONIC - GENERAL LEDGER REPORT\n";
    csvContent += `Generated On,${new Date().toLocaleDateString()}\n\n`;
    csvContent += "Txn ID,Date,Voucher No,Type,Effect,Account,Party / Description,Debit (Dr),Credit (Cr),Running Balance\n";

    filteredEntries.forEach((e) => {
      csvContent += `"${e.transaction_id}","${e.date}","${e.voucher_no}","${e.transaction_type}","${e.entry_type}","${e.account}","${e.description || ""} ${e.party ? `(${e.party})` : ""}",${e.debit},${e.credit},${e.running_balance}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `General_Ledger_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Local Search Filter ────────────────────────────────────
  const filteredEntries = entries.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.voucher_no && item.voucher_no.toLowerCase().includes(q)) ||
      (item.account && item.account.toLowerCase().includes(q)) ||
      (item.party && item.party.toLowerCase().includes(q)) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.transaction_type && item.transaction_type.toLowerCase().includes(q))
    );
  });

  if (!canView("report_ledger")) {
    return (
      <div className="ledger-page p-4 text-center">
        <div className="alert alert-danger p-4 shadow-sm" style={{ maxWidth: "600px", margin: "40px auto", borderRadius: "12px" }}>
          <h4 style={{ fontWeight: "700" }}>Access Denied</h4>
          <p style={{ margin: "12px 0", color: "#7f1d1d" }}>
            You do not have permission to view the General Ledger Report. Please contact your system administrator.
          </p>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ledger-page">
      {/* ── Printable Header ───────────────────────────────── */}
      <div className="ledger-print-header">
        <h1>REKONIC ERP - GENERAL LEDGER REPORT</h1>
        <p>Period: {startDate || "Beginning"} to {endDate || "Current"} | Generated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* ── Header ────────────────────────────────────────── */}
      <div className="ledger-header">
        <div className="ledger-title-group">
          <h2>
            <span className="ledger-title-icon"><FaBook /></span>
            <span>General Ledger Report</span>
          </h2>
          <p>Complete double-entry transaction record, debit/credit audit details, and running account balances</p>
        </div>

        <div className="ledger-header-actions">
          <button className="ledger-btn ledger-btn-export" onClick={handleExportCSV}>
            <FaFileCsv /> Export CSV
          </button>
          <button className="ledger-btn ledger-btn-print" onClick={handlePrint}>
            <FaPrint /> Print / PDF
          </button>
          <button className="ledger-btn ledger-btn-back" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────── */}
      {summary && (
        <div className="ledger-summary">
          <div className="summary-card total">
            <div className="card-top">
              <span className="card-icon text-primary"><FaListOl /></span>
              <span className="card-badge badge-primary">Total Count</span>
            </div>
            <div className="card-label">Total Transactions</div>
            <div className="card-value">{summary.total_transactions ?? 0}</div>
          </div>
          <div className="summary-card debit">
            <div className="card-top">
              <span className="card-icon text-success"><FaArrowUp /></span>
              <span className="card-badge badge-success">Debit Effect</span>
            </div>
            <div className="card-label">Total Debit (Dr)</div>
            <div className="card-value">${fmt(summary.total_debit)}</div>
          </div>
          <div className="summary-card credit">
            <div className="card-top">
              <span className="card-icon text-warning"><FaArrowDown /></span>
              <span className="card-badge badge-warning">Credit Effect</span>
            </div>
            <div className="card-label">Total Credit (Cr)</div>
            <div className="card-value">${fmt(summary.total_credit)}</div>
          </div>
          <div className="summary-card balance">
            <div className="card-top">
              <span className="card-icon text-info"><FaBalanceScale /></span>
              <span className="card-badge badge-info">Net Ledger</span>
            </div>
            <div className="card-label">Net Balance</div>
            <div className="card-value">
              ${fmt(Math.abs(summary.net_balance))}
              <span className="balance-tag">
                {summary.net_balance >= 0 ? "Dr" : "Cr"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters Card ───────────────────────────────────── */}
      <div className="ledger-filter-card">
        <div className="ledger-filter-form">
          <div className="filter-group">
            <label><FaCalendarAlt className="me-1" /> From Date</label>
            <input
              id="ledger-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label><FaCalendarAlt className="me-1" /> To Date</label>
            <input
              id="ledger-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Transaction Type</label>
            <select
              id="ledger-type-filter"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              {TRANSACTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Party / Customer / Supplier</label>
            <input
              id="ledger-party-filter"
              type="text"
              placeholder="Search by name…"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchReport()}
            />
          </div>

          <div className="filter-group search-group">
            <label><FaSearch className="me-1" /> Instant Search</label>
            <input
              type="text"
              placeholder="Voucher #, Account, Description…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button id="ledger-apply-btn" className="ledger-btn ledger-btn-filter" onClick={fetchReport}>
              <FaFilter /> Filter
            </button>
            <button id="ledger-reset-btn" className="ledger-btn ledger-btn-reset" onClick={handleReset}>
              <FaUndo /> Reset
            </button>
          </div>
        </div>

        {/* Shortcuts Bar */}
        <div className="ledger-presets-bar">
          <span className="ledger-presets-label">Shortcuts:</span>
          <button type="button" className="ledger-preset-chip" onClick={() => handlePreset("THIS_MONTH")}>
            This Month
          </button>
          <button type="button" className="ledger-preset-chip" onClick={() => handlePreset("THIS_QUARTER")}>
            This Quarter
          </button>
          <button type="button" className="ledger-preset-chip" onClick={() => handlePreset("YTD")}>
            Year to Date (YTD)
          </button>
        </div>
      </div>

      {/* ── Table Content ──────────────────────────────────── */}
      {loading ? (
        <div className="ledger-loading">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p>Loading General Ledger Entries…</p>
        </div>
      ) : error ? (
        <div className="ledger-empty text-danger">
          <p><FaExclamationCircle className="me-2" /> {error}</p>
          <button className="ledger-btn ledger-btn-filter" onClick={fetchReport}>Retry</button>
        </div>
      ) : (
        <LedgerTable entries={filteredEntries} summary={summary} />
      )}

      {/* ── Double-Entry Rule Guide Footer ──────────────────── */}
      <div className="ledger-rules-card">
        <strong className="rules-title">
          DOUBLE-ENTRY ACCOUNTING POSTING RULES
        </strong>
        <div className="rules-grid">
          {[
            { type: "Sale",        dr: "Accounts Receivable",       cr: "Sales Revenue" },
            { type: "Receipt",     dr: "Cash / Bank",               cr: "Accounts Receivable" },
            { type: "Payment",     dr: "Expense Account",           cr: "Cash / Bank" },
            { type: "Refund",      dr: "Sales Returns & Allowances",cr: "Cash / Bank" },
            { type: "Credit Note", dr: "Sales Returns",             cr: "Accounts Receivable" },
            { type: "Debit Note",  dr: "Accounts Payable",          cr: "Purchase Returns" },
          ].map((row) => (
            <div key={row.type} className="rule-item">
              <span className={`type-badge type-${row.type.replace(" ", "")}`}>
                {row.type}
              </span>
              <span className="rule-dr">Dr</span>
              <span>{row.dr}</span>
              <span className="rule-sep">|</span>
              <span className="rule-cr">Cr</span>
              <span>{row.cr}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LedgerReportPage;
